import React, { useState, useEffect, useCallback } from 'react';
import {
  MdRefresh, MdCheckCircle, MdError, MdNewspaper, MdAccessTime,
  MdSpeed, MdCached, MdCloudDownload,
} from 'react-icons/md';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatBlock({ label, value, sub, icon, tone = 'sky', testId }) {
  const tones = {
    sky: 'bg-sky-50 border-sky-200 text-sky-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`border rounded-xl p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-rubik uppercase tracking-wide opacity-70">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-bold font-rubik">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function PeriodPanel({ title, stats, testIdPrefix }) {
  if (!stats) return null;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid={`${testIdPrefix}-panel`}>
      <h3 className="text-lg font-rubik font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBlock
          label="Total fetches"
          value={stats.total_fetches}
          icon={<MdCloudDownload className="text-sky-500 text-lg" />}
          tone="sky"
          testId={`${testIdPrefix}-total`}
        />
        <StatBlock
          label="Success rate"
          value={`${stats.success_rate}%`}
          sub={`${stats.successes} ok · ${stats.failures} fail`}
          icon={<MdCheckCircle className="text-emerald-500 text-lg" />}
          tone={stats.success_rate >= 90 ? 'emerald' : stats.success_rate >= 70 ? 'amber' : 'rose'}
          testId={`${testIdPrefix}-success`}
        />
        <StatBlock
          label="Cache hit rate"
          value={`${stats.cache_hit_rate}%`}
          sub={`${stats.cache_hits} hits · ${stats.live_fetches} live`}
          icon={<MdCached className="text-amber-500 text-lg" />}
          tone="amber"
          testId={`${testIdPrefix}-cache`}
        />
        <StatBlock
          label="Items pulled"
          value={stats.items_pulled}
          sub={`avg ${stats.avg_latency_ms_live} ms / live fetch`}
          icon={<MdSpeed className="text-slate-500 text-lg" />}
          tone="slate"
          testId={`${testIdPrefix}-items`}
        />
      </div>
    </div>
  );
}

export default function AdminRSSHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/rss-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setErr(e.message || 'Failed to load RSS health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-rss-health-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-rubik flex items-center gap-2">
            <MdNewspaper className="text-sky-600" /> RSS Feed Health
          </h1>
          <p className="text-sm text-gray-500 mt-1 font-rubik">
            Google News RSS — Related News fetches across Bills, Polls, and Homepage.
          </p>
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-rubik hover:bg-sky-700 disabled:opacity-50"
          data-testid="admin-rss-refresh-btn"
        >
          <MdRefresh className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 text-sm font-rubik" data-testid="admin-rss-error">
          <MdError className="inline mr-2" /> {err}
        </div>
      )}

      {data && (
        <>
          {/* Quick status row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-rss-status-row">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-rubik">Library</div>
              <div className={`text-lg font-bold ${data.feedparser_available ? 'text-emerald-600' : 'text-rose-600'}`}>
                {data.feedparser_available ? 'feedparser OK' : 'feedparser missing'}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-rubik">Cache entries</div>
              <div className="text-lg font-bold text-gray-800">{data.cache_entries}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-rubik flex items-center gap-1">
                <MdAccessTime className="text-xs" /> Last fetch
              </div>
              <div className="text-sm font-rubik text-gray-800 mt-0.5">
                {data.last_fetched_at ? new Date(data.last_fetched_at).toLocaleString() : 'Never'}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-rubik">Source</div>
              <a
                href={data.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-rubik text-sky-600 underline"
              >
                Google News RSS
              </a>
            </div>
          </div>

          <PeriodPanel title="Last 24 hours" stats={data.last_24h} testIdPrefix="admin-rss-24h" />
          <PeriodPanel title="Last 7 days" stats={data.last_7d} testIdPrefix="admin-rss-7d" />

          {/* Top queries */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="admin-rss-top-queries">
            <h3 className="text-lg font-rubik font-semibold text-gray-900 mb-3">Most-requested queries (24h)</h3>
            {data.top_queries_24h?.length ? (
              <ul className="divide-y divide-gray-100">
                {data.top_queries_24h.map((q, i) => (
                  <li key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`admin-rss-query-${i}`}>
                    <span className="font-rubik text-gray-800 truncate">{q.query}</span>
                    <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs font-bold">{q.count}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 italic">No queries logged in the last 24 hours yet.</p>
            )}
          </div>

          {/* Recent failures */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="admin-rss-failures">
            <h3 className="text-lg font-rubik font-semibold text-gray-900 mb-3">Recent failures</h3>
            {data.recent_failures?.length ? (
              <ul className="space-y-2">
                {data.recent_failures.map((f, i) => (
                  <li
                    key={i}
                    className="border border-rose-200 bg-rose-50 rounded-lg px-3 py-2 text-xs font-rubik"
                    data-testid={`admin-rss-failure-${i}`}
                  >
                    <div className="font-semibold text-rose-700 truncate">{f.query || '(empty query)'}</div>
                    <div className="text-rose-600 mt-0.5 truncate">{f.error || 'Unknown error'}</div>
                    <div className="text-rose-500 mt-0.5 text-[10px]">
                      {f.fetched_at ? new Date(f.fetched_at).toLocaleString() : ''}
                      {f.latency_ms ? ` · ${f.latency_ms} ms` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-emerald-600 italic">No failures recorded. </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
